import { Spin, message } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { convertToDoorAddress, getDeviceObjectTypeBy, getFullVelocityUrl, handleResponse } from '../../../Helper';
import { deviceAdminApi, graphicApi } from '../../../api';
import { SubPermissions, User } from '../../../model/AccountModel';
import { AlarmModel } from '../../../model/AlarmModel';
import { DigitracAvailableOptions, DigitracDisabledOptions, GraphicModeOption, RedlineConfigValues, ResponseObjectEntity } from '../../../model/CommonModel';
import { ActionDigitrac, ActionDigitracContextMenu, CurrentDeviceControlObj, DeviceObjectType, ObjectTypes, PortStyle } from '../../../model/DeviceAdminModel';
import {
	ActionGraphic,
	ActionGraphicContextMenu,
	ChangePMZStateIndex,
	ContextMenuObjTypes,
	ContextMenuPosition,
	Coordinate,
	GraphicActionType,
	GraphicChangeType,
	GraphicCommandSet,
	GraphicLink,
	GraphicLiveMessage,
	GraphicMessage,
	GraphicPageMode,
	GraphicText,
	IntrinsicEvent,
	MonitoringPoint,
	ObjectLibraryFolder,
	ObjectLibraryTreeNode,
	PointMonitoringZone,
	PointState,
	RedLine,
	ReplacePathInfo,
	SpaceObject,
	SpaceObjectSerialize,
	SpaceObjectType,
	SpecialEventType,
	VelocityObject,
} from '../../../model/GraphicModel';
import { PhotoCallUp } from '../../../model/PhotoCallUpModel';
import { MapDesignCircleIcon, MapDesignIcon, PublishCircleIcon, PublishIcon } from '../../../resources/Icons';
import { useStoreDispatch, useStoreSelector } from '../../../store';
import { setShowAlarmDetails } from '../../../store/alarm/actions';
import { selectShowAddOperatorNote, selectShowAlarmDetails } from '../../../store/alarm/selectors';
import { setCurrentDevice } from '../../../store/common/actions';
import {
	selectAckAlarmList,
	selectAlarmList,
	selectClearedAlarmList,
	selectCurrentDevice,
	selectPhotoCallCredentials,
	selectViewSettings,
} from '../../../store/common/selectors';
import {
	disableMenuOptsAction,
	saveGraphicPreferences,
	setCurrentRedlineConfigValuesAction,
	setDropDataNodesAction,
	setLastAlarmQueueCoordinatesAction,
	setLastObjectLibraryCoordinatesAction,
	setLastPlottedDevicesCoordinatesAction,
	setMapHasChangedAction,
	setMapMode,
	switchFloatingWindowVisibilityAction,
} from '../../../store/graphic/actions';
import {
	alreadyLoadedSelector,
	currentRedlineConfigValuesSelector,
	drawingMenuOptsDisabledSelector,
	dropDataNodesSelector,
	graphicActionTypeSelector,
	graphicEventsSelector,
	isAlarmQueueOpenedSelector,
	isBirdEyeOpenedSelector,
	isDevicesOpenedSelector,
	isObjectPropertiesOpenedSelector,
	isPlottedDeviceOpenedSelector,
	isQuickAccessOpenedSelector,
	lastAlarmQueueCoordinatesSelector,
	lastObjectDirectoryCoordinatesSelector,
	lastPlottedDevicesCoordinatesSelector,
	mapInfoSelector,
	mapModeSelector,
	spaceObjectSelector,
} from '../../../store/graphic/selectors';
import { AddOperatorNoteModal, AlarmDetailsModal } from '../../alarms';
import { GraphicModeMenu, canvasContainerId, canvasMainContainerId } from '../../common';
import { getContextMenuGenericOptions } from '../../devicecontrol/DigiTrac/helpers';
import { AlarmQueue, AlarmTableModal, ContextMenu, FloatingItems, MenuBar, ObjectLibrary } from '../../graphic';
import { GraphicPhotoCallUpCard } from '../CustomPhotoCallUpCard/GraphicPhotoCallUpCard';
import { MainMenu } from '../MenuBar/MainMenu/MainMenu';
import {
	addDeviceSpaceObject,
	buildAlarmOptions,
	buildDigiTracActionControlOptions,
	buildMonitorOptions,
	getConfigureDeviceModal,
	getCurrentVelocityObject,
	getDraggableItems,
	getVelocityObjectType,
	handleIconStateChange,
	paintPMZ,
	proccessLiveAlarmEvent,
	sendDeviceCommand,
	setAlarmState,
} from '../helper';
import styles from './mapDesigner.module.scss';

const velocityUrl = getFullVelocityUrl();
const showLiveEvents = graphicsLiveEvents();
const user: User = getUser();
const canUseAlarmViewer: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmViewer_Use).allowed;
const photoCallUpCardId = 'photoCallUpCard';
const modeMenuOptions: GraphicModeOption[] = [
	{
		value: GraphicPageMode.Design,
		text: _('Design'),
		hasPermission: User.getSubComponentPermission(user, SubPermissions.Maps_Maps_UseDesignMode).allowed,
		SelectedIcon: <MapDesignCircleIcon />,
		UnSelectedIcon: <MapDesignIcon />,
	},
	{
		value: GraphicPageMode.Live,
		text: _('Monitor'),
		hasPermission: true,
		SelectedIcon: <PublishIcon />,
		UnSelectedIcon: <PublishCircleIcon />,
	},
];
const spaceObjectHeight = 48;
const spaceObjectWidth = 64;

const MapDesigner: React.FC = () => {
	const dispatch = useStoreDispatch();
	const [messageApi, contextHolder] = message.useMessage();

	const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition>({
		x: 0,
		y: 0,
	});
	const [displayContextMenu, setDisplayContextMenu] = useState<boolean>(false);
	const [contextMenuItems, setContextMenuItems] = useState([]);
	const [showAlarmTableModal, setShowAlarmTableModal] = useState<boolean>(false);
	const [alarmsAction, setAlarmsAction] = useState<ActionGraphicContextMenu>(ActionGraphicContextMenu.NONE);
	const [currentDeviceForConfiguration, setCurrentDeviceForConfiguration] = useState<JSX.Element>(null);
	const [addedSpaceObjectsIds, setAddedSpaceObjectsIds] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [objectDirectoryLibrary, setObjectDirectoryLibrary] = useState<ObjectLibraryTreeNode>(undefined);
	const [canvasMainContainerCoordinates, setCanvasMainContainerCoordinates] = useState<Coordinate>({
		x: 0,
		y: 0,
	});
	const [onLoadEndFinished, setOnLoadEndFinished] = useState<boolean>(false);
	const [selectedObject, setSelectedObject] = useState<string>(undefined);

	const mode = useStoreSelector(mapModeSelector);
	const graphicMessages: GraphicMessage[] = useStoreSelector<GraphicMessage[]>(graphicEventsSelector);
	const { Id, Name } = useStoreSelector(mapInfoSelector);
	const dropDataNodes = useStoreSelector<ObjectLibraryTreeNode[]>(dropDataNodesSelector);
	const profileSettings = useStoreSelector(selectViewSettings);
	const currentRedlineConfigValues = useStoreSelector<RedlineConfigValues>(currentRedlineConfigValuesSelector);
	const spaceObject = useStoreSelector<Partial<SpaceObjectSerialize>>(spaceObjectSelector);
	const showAlarmAddOperatorNoteModal: boolean = useStoreSelector<boolean>(selectShowAddOperatorNote);
	const isDevicesOpened = useStoreSelector(isDevicesOpenedSelector);
	const isBirdEyeOpened = useStoreSelector(isBirdEyeOpenedSelector);
	const isObjectPropertiesOpened = useStoreSelector(isObjectPropertiesOpenedSelector);
	const isPlottedDeviceOpened = useStoreSelector(isPlottedDeviceOpenedSelector);
	const isQuickAccessOpened = useStoreSelector(isQuickAccessOpenedSelector);
	const isAlarmQueueOpened = useStoreSelector(isAlarmQueueOpenedSelector);
	const lastObjectLibraryCoordinates = useStoreSelector(lastObjectDirectoryCoordinatesSelector);
	const lastPlottedDevicesCoordinates = useStoreSelector(lastPlottedDevicesCoordinatesSelector);
	const lastAlarmQueueCoordinates = useStoreSelector(lastAlarmQueueCoordinatesSelector);
	const preferencesAlreadyLoaded = useStoreSelector(alreadyLoadedSelector);
	const currentDevice: CurrentDeviceControlObj = useStoreSelector<CurrentDeviceControlObj>(selectCurrentDevice);
	const photoCallCredentials: PhotoCallUp[] = useStoreSelector<PhotoCallUp[]>(selectPhotoCallCredentials);
	const realTimeAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectAlarmList);
	const realTimeAckAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectAckAlarmList);
	const realTimeClearedAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectClearedAlarmList);
	const showAlarmDetails: boolean = useStoreSelector<boolean>(selectShowAlarmDetails);
	const disabledOpts: boolean = useStoreSelector<boolean>(drawingMenuOptsDisabledSelector);
	const graphicActionType: GraphicActionType = useStoreSelector<GraphicActionType>(graphicActionTypeSelector);
	const disableOpts: boolean = useStoreSelector<boolean>(drawingMenuOptsDisabledSelector);

	const spaceObjectId = useRef('');
	const spaceObjectType = useRef<SpaceObjectType>(undefined);
	const velocityObjectsReference = useRef<Partial<VelocityObject>[]>([]);
	const pointMonitoringZonesReference = useRef<PointMonitoringZone[]>([]);
	const commandSetsReference = useRef<GraphicCommandSet[]>([]);
	const linksReference = useRef<GraphicLink[]>([]);
	const textsReference = useRef<GraphicText[]>([]);
	const room = useRef<any>(undefined);
	const textLayer = useRef<any>(undefined);
	const graphicPageMode = useRef<GraphicPageMode>();

	const objectDirectoryLibraryRef = useRef<ObjectLibraryTreeNode>();
	const setObjectDirectoryLibraryRef = (newObjectDirectoryLibraryRef: ObjectLibraryTreeNode): void => {
		objectDirectoryLibraryRef.current = newObjectDirectoryLibraryRef;
	};
	const getObjectDirectoryLibraryRef = (): ObjectLibraryTreeNode => {
		return objectDirectoryLibraryRef.current;
	};

	const getGraphicPageMode = () => graphicPageMode.current;

	const setGraphicPageMode = value => (graphicPageMode.current = value);

	const isDesignMode = useMemo(() => mode === GraphicPageMode.Design, [mode]);
	const selectedVelocityObject = useMemo(() => getCurrentVelocityObject(selectedObject), [selectedObject]);
	const selectedVelocityObjectType = useMemo(() => getVelocityObjectType(selectedObject), [selectedObject]);

	const loadObjectDirectoryLibrary = isDesignMode && isDevicesOpened && preferencesAlreadyLoaded;
	const loadPlottedDevice = isDesignMode && isPlottedDeviceOpened && preferencesAlreadyLoaded;
	const loadAlarmQueue = !isDesignMode && canUseAlarmViewer && isAlarmQueueOpened && preferencesAlreadyLoaded;
	const showAlarmQueue: boolean = loadAlarmQueue;

	const callbackMO = (mutations: MutationRecord[]) => {
		for (const mutation of mutations) {
			if (mutation.type === 'childList') {
				if (mutation.addedNodes.length > 0) {
					const nd = mutation.addedNodes?.[0] as HTMLElement;
					if (nd.id && nd.id.startsWith('cv_NODE_') && nd.tagName === 'g' && !nd.dataset.ctx) {
						nd.dataset.ctx = 'true';
						nd.addEventListener('contextmenu', addContextMenu);
					}
				}
			}
		}
	};

	const observer = new MutationObserver(callbackMO);

	const addContextMenu = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		setContextMenuPosition({
			x: e.clientX,
			y: e.clientY,
		});
		document.addEventListener('click', closeContextMenu);
		const isDesignMode = getGraphicPageMode() === GraphicPageMode.Design;
		const contextMenuOptions = isDesignMode ? getDesignContextMenu() : getMonitorContextMenu();

		batch(() => {
			setContextMenuItems(contextMenuOptions);
			setDisplayContextMenu(true);
		});
	};

	useEffect(() => {
		graphicApi.getObjectLibrary().then((response: ResponseObjectEntity<ObjectLibraryTreeNode>) => {
			if (!handleResponse(response)) {
				batch(() => {
					const { Entity } = response;
					setObjectDirectoryLibrary(Entity);
					setObjectDirectoryLibraryRef(JSON.parse(JSON.stringify(Entity)));
				});
			}
		});

		return () => {
			document.removeEventListener('click', closeContextMenu);
			observer.disconnect();
		};
	}, []);

	useEffect(() => {
		if (mode !== getGraphicPageMode()) {
			setGraphicPageMode(mode);
		}
		if (onLoadEndFinished) {
			if (mode === GraphicPageMode.Design) {
				messageApi.destroy(photoCallUpCardId);
				if (velocityObjectsReference.current?.length > 0) {
					velocityObjectsReference.current.forEach(vo => handleIconStateChange(SpecialEventType.ClearedAlarm, vo.UniqueId));
				}
			} else {
				if (velocityObjectsReference.current?.length > 0) {
					velocityObjectsReference.current.forEach(vo => setAlarmState(vo));
				}
			}
		}
	}, [mode]);

	useEffect(() => {
		if (Name) {
			const { VelocityObjects, PointMonitoringZones, CommandSets, Links, Texts, FileName: MapFileName } = spaceObject;
			velocityObjectsReference.current = VelocityObjects.map(x => ({ ...x, UniqueId: getGuid() }));
			pointMonitoringZonesReference.current = PointMonitoringZones;
			commandSetsReference.current = CommandSets;
			linksReference.current = Links;
			textsReference.current = Texts;

			const fileName: string = `${velocityUrl}/Content/Graphics/${MapFileName}`;
		}
	}, [Name]);

	useEffect(() => {
		//TODO removed until reworked
		if (showLiveEvents && graphicMessages && graphicMessages.length) {
			//TODO if there is a way to freeze events, add them to pending changes
			graphicMessages.forEach((message: GraphicMessage) => {
				switch (message.ChangeType) {
					case GraphicChangeType.LiveEvent:
						const event: GraphicLiveMessage = message.LiveMessage;
						if (event.IntrinsicEvents && event.IntrinsicEvents.length > 0) {
							event.IntrinsicEvents.forEach((intrinsicEvent: IntrinsicEvent) => {
								velocityObjectsReference.current?.forEach(x => {
									if (x.Address === intrinsicEvent.Address || (intrinsicEvent.DoorAddress && x.Address === intrinsicEvent.DoorAddress)) {
										handleIconStateChange(intrinsicEvent.SpecialValue, x.UniqueId);
									}
								});

								if (intrinsicEvent.EventId === 4023 || intrinsicEvent.EventId === 4024) {
									let changeStateIndexes: ChangePMZStateIndex[] = [];
									let inputIndex = -1;
									pointMonitoringZonesReference.current.forEach((x, index) => {
										if (x.Points?.length > 0) {
											inputIndex = x.Points.findIndex(y => y.Address === intrinsicEvent.Address);
											if (inputIndex !== -1) {
												changeStateIndexes.push({ pmzIndex: index, inputIndex: inputIndex });
											}
										}
									});

									changeStateIndexes.forEach(({ inputIndex, pmzIndex }: ChangePMZStateIndex) => {
										if (pmzIndex !== -1) {
											let monitoringPoint: MonitoringPoint = pointMonitoringZonesReference.current[pmzIndex].Points[inputIndex];
											switch (intrinsicEvent.SpecialValue) {
												case SpecialEventType.ContactOneClosed:
													monitoringPoint.Contact1Secured = PointState.PMZ_POINT_STATE_TRUE;
													break;
												case SpecialEventType.ContactOneOpen:
													monitoringPoint.Contact1Secured = PointState.PMZ_POINT_STATE_FALSE;
													break;
												case SpecialEventType.ContactThreeClosed:
													monitoringPoint.Contact3Secured = PointState.PMZ_POINT_STATE_FALSE;
													break;
												case SpecialEventType.ContactThreeOpen:
													monitoringPoint.Contact3Secured = PointState.PMZ_POINT_STATE_TRUE;
													break;
												case SpecialEventType.InputMasked:
													monitoringPoint.MaskState = PointState.PMZ_POINT_STATE_TRUE;
													break;
												case SpecialEventType.InputUnmasked:
													monitoringPoint.MaskState = PointState.PMZ_POINT_STATE_FALSE;
													break;
												case SpecialEventType.Unknown:
													monitoringPoint.Contact1Secured = PointState.PMZ_POINT_STATE_UNKNOWN;
													break;
											}
											pointMonitoringZonesReference.current[pmzIndex].Points[inputIndex] = monitoringPoint;

											paintPMZ([monitoringPoint], pointMonitoringZonesReference.current[pmzIndex], room.current);
										}
									});
								}
							});
						}

						break;

					case GraphicChangeType.LiveAlarm:
						//TODO: add to alarmQueue
						break;
				}
			});
		}
	}, [graphicMessages]);

	useEffect(() => {
		if (!dropDataNodes?.length) {
			return;
		}

		addDeviceSpaceObjects();
	}, [dropDataNodes]);

	useEffect(() => {
		if (profileSettings && profileSettings.length > 0) {
			// TODO: build logic to set this prop into redux
			const devicesWindowPosition = profileSettings.find(s => s.Name === 'DevicesWindowPosition')?.Value.toString() || '0,0';
			const plottedDevicesWindowPosition = profileSettings.find(s => s.Name === 'PlottedDevicesWindowPosition')?.Value.toString() || '0,0';
			const alarmQueueWindowPosition = profileSettings.find(s => s.Name === 'AlarmQueueWindowPosition')?.Value.toString() || '0,0';
			const birdEye = profileSettings.find(s => s.Name === 'IsVisibleBirdEyeWindow')?.Value === 'True';
			const devices = profileSettings.find(s => s.Name === 'IsVisibleDevicesWindow')?.Value === 'True';
			const objectProperties = profileSettings.find(s => s.Name === 'IsVisibleObjectPropertiesWindow')?.Value === 'True';
			const plottedDevices = profileSettings.find(s => s.Name === 'IsVisiblePlottedDevicesWindow')?.Value === 'True';
			const quickAccess = profileSettings.find(s => s.Name === 'IsVisibleQuickAccessWindow')?.Value === 'True';
			let arrPosition = devicesWindowPosition.split(',');
			let objectDirectoryXValue = -1;
			let objectDirectoryYValue = -1;
			if (arrPosition.length > 1) {
				objectDirectoryXValue = parseInt(arrPosition[0]);
				objectDirectoryYValue = parseInt(arrPosition[1]);
			}

			arrPosition = plottedDevicesWindowPosition.split(',');
			let plottedDevicesXValue = -1;
			let plottedDevicesYValue = -1;
			if (arrPosition.length > 1) {
				plottedDevicesXValue = parseInt(arrPosition[0]);
				plottedDevicesYValue = parseInt(arrPosition[1]);
			}

			arrPosition = alarmQueueWindowPosition.split(',');
			let alarmQueueXValue = -1;
			let alarmQueueYValue = -1;
			if (arrPosition.length > 1) {
				alarmQueueXValue = parseInt(arrPosition[0]);
				alarmQueueYValue = parseInt(arrPosition[1]);
			}

			batch(() => {
				dispatch(
					switchFloatingWindowVisibilityAction({
						birdEye,
						devices,
						objectProperties,
						plottedDevices,
						quickAccess,
						alreadyLoaded: true,
					})
				);
				dispatch(
					setLastObjectLibraryCoordinatesAction({
						x: objectDirectoryXValue,
						y: objectDirectoryYValue,
					})
				);
				dispatch(
					setLastPlottedDevicesCoordinatesAction({
						x: plottedDevicesXValue,
						y: plottedDevicesYValue,
					})
				);
				dispatch(
					setLastAlarmQueueCoordinatesAction({
						x: alarmQueueXValue,
						y: alarmQueueYValue,
					})
				);
			});
		}
	}, [profileSettings]);

	useEffect(() => {
		if (!isDevicesOpened && objectDirectoryLibrary) {
			setObjectDirectoryLibraryRef(JSON.parse(JSON.stringify(objectDirectoryLibrary)));
		}
	}, [isDevicesOpened]);

	useEffect(() => {
		if (!isDesignMode && velocityObjectsReference.current?.length > 0 && photoCallCredentials?.length > 0) {
			photoCallCredentials.forEach((photoCallUp, index) => {
				const voObj = velocityObjectsReference.current.find(vo => vo.ObjectId === photoCallUp.DoorId);
				if (voObj) {
					const spaceObj: SpaceObject = null;
					if (spaceObj) {
						const voNode: HTMLElement = document.querySelector(`g[id*='${spaceObj.node}']`);
						if (voNode) {
							checkDoorNodeLimits(voNode, spaceObj.id);
							messageApi.open({
								content: '...',
								key: photoCallUpCardId,
								style: {
									display: 'none',
									padding: 0,
									position: 'absolute',
									zIndex: 101,
								},
								type: 'loading',
							});
							deviceAdminApi
								.getDoorPhotoCallUpDelay(photoCallUp.DoorId)
								.then(response => {
									if (handleResponse(response)) {
										messageApi.destroy(photoCallUpCardId);
									}
									const arrowId: string = `${photoCallUpCardId}Arrow`;
									messageApi.open({
										className: styles.photoCallUpMessage,
										content: (
											<GraphicPhotoCallUpCard
												arrowId={arrowId}
												closeCard={handleClosePhotoCallUpCard}
												doorNode={voNode}
												index={index}
												photoCallUp={photoCallUp}
											/>
										),
										duration: response.Entity || 6,
										key: photoCallUpCardId,
										onClose: () => {
											const arrow = document.querySelector(`[id=${arrowId}]`);
											if (arrow) {
												arrow.remove();
											}
										},
										style: {
											display: 'none',
											padding: 0,
											position: 'absolute',
											zIndex: 101,
										},
									});
								})
								.catch(() => {
									messageApi.destroy(photoCallUpCardId);
								});
						}
					}
				}
			});
		}
	}, [photoCallCredentials]);

	useEffect(() => {
		//TODO: FIX THIS
		if (!showAlarmQueue && (realTimeAlarmList.length || realTimeAckAlarmList.length || realTimeClearedAlarmList.length)) {
			const alarm: AlarmModel = [...realTimeAckAlarmList, ...realTimeAlarmList, ...realTimeClearedAlarmList][0];
			const isNormalAlarm: boolean = realTimeAlarmList?.length > 0;
			let address: string = alarm.Address,
				doorAddress: string = convertToDoorAddress(address);
			const velocityObject: Partial<VelocityObject> = velocityObjectsReference.current?.find(vo => vo.Address === address || vo.Address === doorAddress);
			proccessLiveAlarmEvent({ alarmAddress: alarm.Address, isNormalAlarm, velocityObject, isVelocityObjectUpdated: false });
		}
	}, [realTimeAlarmList, realTimeAckAlarmList, realTimeClearedAlarmList]);

	useEffect(() => {
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [
		isDevicesOpened,
		isBirdEyeOpened,
		isObjectPropertiesOpened,
		isPlottedDeviceOpened,
		isQuickAccessOpened,
		lastObjectLibraryCoordinates,
		lastPlottedDevicesCoordinates,
	]);

	const checkDoorNodeLimits = (doorNode: HTMLElement, spaceObjectId: string) => {
		const mainCanvasNode = document.getElementById(canvasMainContainerId);
		if (mainCanvasNode) {
			const mainCanvasNodePosition = mainCanvasNode.getBoundingClientRect();
			const voNodePosition = doorNode.getBoundingClientRect();
			if (
				voNodePosition.top < mainCanvasNodePosition.top ||
				voNodePosition.top > mainCanvasNodePosition.bottom ||
				voNodePosition.left < mainCanvasNodePosition.left ||
				voNodePosition.right > mainCanvasNodePosition.right
			) {
			}
		}
	};

	const handleClosePhotoCallUpCard = () => {
		messageApi.destroy(photoCallUpCardId);
	};

	const appendVelocityObjects = (items: Partial<VelocityObject>[]) => {
		items.forEach(x => {
			velocityObjectsReference.current.push(x);
		});
	};

	const handleBeforeUnload = () => {
		dispatch(saveGraphicPreferences());
	};

	const getRedlineThickness = () => {
		const input = document.getElementById('cvjs_redlineThicknessInput') as HTMLInputElement;
		batch(() => {
			dispatch(
				setCurrentRedlineConfigValuesAction({
					...currentRedlineConfigValues,
					width: parseInt(input.value),
				})
			);
			setDisableMenuActions();
		});
	};

	const handleOnDrop = (event: DragEvent) => {
		// prevent any default action
		event.preventDefault();
		const { currentTarget, clientX, clientY, dataTransfer } = event;
		const currentElement = currentTarget as HTMLElement;

		if (currentElement.id === canvasContainerId) {
			if (event.dataTransfer.types.includes('custom-type')) {
				const dataString = dataTransfer.getData('custom-type');
				const data = JSON.parse(dataString) as ObjectLibraryTreeNode;

				const obj: Partial<VelocityObject> = buildVelocityObject(data);
				addDeviceSpaceObject(obj, clientX, clientY);
				appendVelocityObjects([obj]);
			}
		}
	};

	const cvjs_ObjectSelected = (rMid: string) => {
		spaceObjectId.current = rMid;
		const so = null;
		spaceObjectType.current = {
			...contextMenuPosition,
			type: ContextMenuObjTypes.VIO,
			//TODO: FIX
			//secondaryType: VOType.VOT_DOOR,
			secondaryType: 1,
		};
		setSelectedObject(rMid);
	};

	const cvjs_CanvasClick = (id, handle, entity) => {
		setSelectedObject('');
	};

	const closeContextMenu = (e?: MouseEvent | React.MouseEvent<HTMLElement>) => {
		e?.stopPropagation();
		e?.preventDefault();

		document.removeEventListener('click', closeContextMenu);
		setDisplayContextMenu(false);
	};

	const getSpaceObjectSelected = () => velocityObjectsReference.current.find(obj => obj.UniqueId === spaceObjectId.current);

	const getMonitorContextMenu = () => {
		const notValidDeviceForContextMenu = [ObjectTypes.readers];
		const spaceObjectSelected = getSpaceObjectSelected();
		if (spaceObjectSelected === undefined) {
			return;
		}

		const { ObjectType } = spaceObjectSelected;

		if (notValidDeviceForContextMenu.includes(ObjectType)) {
			return;
		}

		const controlPermission: boolean = User.getSubComponentPermission(user, SubPermissions.AdministrationWindow_AdministrationWindow_UseFunctions).allowed;
		const notValidDevicesForControlOptions = [ObjectTypes.pollingPorts, ObjectTypes.xboxes, ObjectTypes.hardwareControllers];
		const disabledOptions: DigitracDisabledOptions = {
			isControlDisabled: !controlPermission,
		};
		const notValidActionOption = [ObjectTypes.inputs, ObjectTypes.expansionInputs];
		const availableOptions: DigitracAvailableOptions = notValidActionOption.includes(ObjectType)
			? {
					isActionOptionsAvailable: false,
			  }
			: {
					isActionOptionsAvailable: true,
			  };

		const alarmsOptions = buildAlarmOptions();
		const controlOptions = !notValidDevicesForControlOptions.includes(ObjectType)
			? buildDigiTracActionControlOptions({ deviceType: ObjectType, disabledOptions, availableOptions, isMapsPage: true })
			: [];

		return [...getContextMenuGenericOptions(alarmsOptions), ...getContextMenuGenericOptions(controlOptions)];
	};

	const getDesignContextMenu = () => {
		return buildMonitorOptions();
	};

	const addDeviceSpaceObjectToMap = (index: number, x: Partial<VelocityObject>): ReplacePathInfo => {
		const width: number = x.PositionInfo.Right - x.PositionInfo.Left;
		const height: number = x.PositionInfo.Bottom - x.PositionInfo.Top;
		const path: string = `M${x.PositionInfo.Left},${x.PositionInfo.Top} h${width} v${height} h-${width} v-${height} Z`;

		addDeviceSpaceObject(x, x.PositionInfo.Left, x.PositionInfo.Top);

		const retVale: ReplacePathInfo = {
			id: `cv_NODE_${index + 1}`,
			path: path,
			x: x.PositionInfo.Left,
			y: x.PositionInfo.Top,
			width: width,
			height: height,
		};
		return retVale;
	};

	const onLoadEnd = () => {
		observer.observe(document.getElementById(canvasMainContainerId), { childList: true, subtree: true });
		room.current = null;
		textLayer.current = null;
		let redLines: RedLine[] = [];
		if (commandSetsReference.current && commandSetsReference.current.length) {
			commandSetsReference.current.forEach(x => {
				// redLines.push()
			});
		}

		if (linksReference.current && linksReference.current.length) {
			linksReference.current.forEach(x => {
				// redLines.push()
			});
		}

		if (textsReference.current && textsReference.current.length) {
			textsReference.current.forEach(x => {
				// redLines.push()
			});
		}

		if (redLines.length) {
		}

		let newPMZs: Partial<SpaceObject>[] = [];
		if (pointMonitoringZonesReference.current && pointMonitoringZonesReference.current.length) {
			pointMonitoringZonesReference.current.forEach(x => {
				newPMZs.push();
			});
		}

		if (newPMZs.length) {
			pointMonitoringZonesReference.current.forEach(x => {
				paintPMZ(x.Points, x, room.current);
			});
		}

		const replacePath: ReplacePathInfo[] = [];

		const draggableItems: ObjectTypes[] = getDraggableItems();
		draggableItems.forEach((x, index) => {
			const fakeObj: Partial<VelocityObject> = {
				PositionInfo: {
					Id: index,
					Right: 150,
					Left: 0,
					Bottom: 200,
					Top: 0,
					Rotation: 0,
				},
				ObjectId: x as number,
				ObjectType: x,
				UniqueId: x.toString(),
			};
			//replacePath.push(addDeviceSpaceObjectToMap(index, fakeObj));
		});

		if (velocityObjectsReference.current && velocityObjectsReference.current.length) {
			velocityObjectsReference.current.forEach((x, index) => {
				replacePath.push(addDeviceSpaceObjectToMap(index, x));
				if (x.InitialState?.length) {
					x.InitialState.forEach(y => handleIconStateChange(y, x.UniqueId));
				}
				setAlarmState(x);
			});
		}

		if (replacePath.length) {
			setTimeout(() => {
				replacePath.forEach(x => {
					const element = document.getElementById(x.id) as HTMLElement;
					element.setAttribute('d', x.path);

					const allImages = document.querySelectorAll(`[id^="${x.id}_image"]`);
					if (allImages?.length) {
						allImages.forEach(y => {
							y.setAttribute('x', x.x.toString());
							y.setAttribute('y', x.y.toString());
							y.setAttribute('width', x.width.toString());
							y.setAttribute('height', x.height.toString());
						});
					}
				});

				draggableItems.forEach(x => {});
			}, 0);
		}

		setOnLoadEndFinished(true);
	};

	const setDisableMenuActions = () => {
		dispatch(disableMenuOptsAction(false));
	};

	const handleOnClickContextMenuAction = async (sender: ActionDigitrac | ActionGraphic) => {
		const { action } = sender;
		const spaceObjectSelected = getSpaceObjectSelected();

		switch (action) {
			case ActionDigitracContextMenu.SEND_COMMAND:
				const { command } = sender;
				sendDeviceCommand(command, spaceObjectSelected);
				break;

			case ActionGraphicContextMenu.ACKNOWLEDGED_ALARMS:
			case ActionGraphicContextMenu.CLEAR_ALARMS:
				batch(() => {
					setAlarmsAction(action);
					setShowAlarmTableModal(true);
				});
				break;

			case ActionGraphicContextMenu.DELETE:
				break;

			case ActionGraphicContextMenu.CONFIGURE_DEVICE:
				openConfigureDevice(spaceObjectSelected);
				break;

			case ActionGraphicContextMenu.SHOW_CREDENTIAL_INFO:
				break;
		}

		closeContextMenu();
	};

	const openConfigureDevice = (spaceObjectSelected: Partial<VelocityObject>) => {
		const { ObjectId, ObjectType, Address } = spaceObjectSelected;

		const newDevice: CurrentDeviceControlObj = {
			...currentDevice,
			Id: ObjectId,
			Address: Address,
			IsModalOpen: !currentDevice.IsModalOpen,
			IsNew: false,
			DeviceObjectType: getDeviceObjectTypeBy(ObjectType),
		};

		dispatch(setCurrentDevice(newDevice));

		const device = getConfigureDeviceModal({
			deviceInfo: newDevice,
			setRedirectPage: () => {},
			setShouldResetSearchColumn: () => {},
			handleCancel: handleOnCloseConfigureDeviceModal,
			handleSave: handleOnCloseConfigureDeviceModal,
		});

		setCurrentDeviceForConfiguration(device);
	};

	const handleOnCloseConfigureDeviceModal = () => {
		setCurrentDeviceForConfiguration(null);
		const newDevice = { Id: 0, DeviceObjectType: DeviceObjectType.Unknown, IsModalOpen: false };
		dispatch(setCurrentDevice(newDevice));
	};

	const buildVelocityObject = (dataNode: ObjectLibraryTreeNode): Partial<VelocityObject> => {
		const {
			displayName,
			extendedData: { objectId, objectType, address },
		} = dataNode;
		const obj: Partial<VelocityObject> = {
			UniqueId: getGuid(),
			Address: address,
			ObjectId: objectId,
			ObjectType: objectType,
			Name: displayName,
			DisplayText: displayName,
			CanvasId: Id,
		};

		return obj;
	};

	const addDeviceSpaceObjects = () => {
		const canvas = document.getElementById(canvasContainerId);
		const position = canvas.getBoundingClientRect();

		let coordinateX = position.left + 80;
		let coordinateY = position.height + 20;

		const newNodes = [];
		dropDataNodes.forEach((dataNode, index) => {
			const obj: Partial<VelocityObject> = buildVelocityObject(dataNode);
			newNodes.push(obj);

			if (!index) {
				addDeviceSpaceObject(obj, coordinateX, coordinateY);
				return;
			}

			// const coordinateX = firstPositionX + index * (spaceObjectHeight / 2);
			// const coordinateY = firstPositionY + index * (spaceObjectWidth / 2);

			coordinateX = coordinateX + spaceObjectHeight / 2;
			coordinateY = coordinateY + spaceObjectWidth / 2;

			addDeviceSpaceObject(obj, coordinateX, coordinateY);
		});

		batch(() => {
			appendVelocityObjects(newNodes);
			dispatch(setDropDataNodesAction([]));
		});
	};

	const getPlottedData = (): ObjectLibraryTreeNode => {
		const retValue: ObjectLibraryTreeNode = {
			key: 'rootNode',
			folderType: ObjectLibraryFolder.FOLDER,
			children: [],
			extendedData: {
				hasMockedData: false,
				objectId: -1,
				objectType: ObjectTypes.none,
				portStyle: PortStyle.NONE,
			},
		};

		if (velocityObjectsReference.current?.length) {
			velocityObjectsReference.current.forEach(x => {
				const { ObjectType, ObjectId, UniqueId } = x;
				const item: ObjectLibraryTreeNode = {
					key: UniqueId,
					displayName: x.DisplayText,
					folderType: ObjectLibraryFolder.ITEM,
					children: [],
					extendedData: {
						hasMockedData: false,
						address: x.Address,
						objectId: ObjectId,
						objectType: ObjectType,
						portStyle: PortStyle.NONE,
					},
				};
				retValue.children.push(item);
			});
		}

		return retValue;
	};

	const objLibraryData = getObjectDirectoryLibraryRef();
	const objPlottedData = getPlottedData();
	const renderPropsWindow = useMemo(() => isDesignMode && selectedObject, [isDesignMode, selectedObject]);

	const handleOnCanvasChanged = () => {
		dispatch(setMapHasChangedAction(true));
	};

	const handleOnChangeContainerCoordinates = (coordinate: Coordinate) => {
		setCanvasMainContainerCoordinates(coordinate);
	};

	const handleOnChangeMode = (mode: number) => {
		dispatch(setMapMode(mode));
	};

	return (
		<>
			<MenuBar>
				<MainMenu
					isMaps={true}
					containerId={canvasContainerId}
					mainContainerId={canvasMainContainerId}
					viewMode={mode}
					redlineConfiguration={currentRedlineConfigValues}
					graphicActionType={graphicActionType}
					disableOptions={disableOpts}
				/>
				<GraphicModeMenu showArrow mode={mode} options={modeMenuOptions} disabled={disabledOpts} onChangeMode={handleOnChangeMode} />
			</MenuBar>
			<div id={canvasMainContainerId} className={styles.canvas}>
				{contextHolder}
				{displayContextMenu && contextMenuItems?.length > 0 && (
					<ContextMenu
						contextMenuItems={contextMenuItems}
						contextMenuPosition={contextMenuPosition}
						handleOnClickContextMenuItem={handleOnClickContextMenuAction}
					/>
				)}
				{showAlarmAddOperatorNoteModal && <AddOperatorNoteModal />}
				{showAlarmTableModal && (
					<AlarmTableModal spaceObjectSelected={getSpaceObjectSelected()} setShowModal={setShowAlarmTableModal} alarmsAction={alarmsAction} />
				)}
				<FloatingItems
					isBirdEyeOpened={isBirdEyeOpened}
					isQuickAccessOpened={isQuickAccessOpened}
					isMaps={true}
					containerId={canvasContainerId}
					mainContainerId={canvasMainContainerId}
					viewMode={mode}
					redlineConfiguration={currentRedlineConfigValues}
					graphicActionType={graphicActionType}
					disableOptions={disableOpts}
				/>
				{loadObjectDirectoryLibrary && objLibraryData && (
					<ObjectLibrary
						data={objLibraryData}
						isObjectDirectory={true}
						initialCoordinates={{
							x: lastObjectLibraryCoordinates.x,
							y: lastObjectLibraryCoordinates.y,
						}}
						parentContainerId={canvasMainContainerId}
						parentCoordinates={canvasMainContainerCoordinates}
					/>
				)}
				{loadPlottedDevice && (
					<ObjectLibrary
						data={objPlottedData}
						isObjectDirectory={false}
						initialCoordinates={{
							x: lastPlottedDevicesCoordinates.x,
							y: lastPlottedDevicesCoordinates.y,
						}}
						parentContainerId={canvasMainContainerId}
						parentCoordinates={canvasMainContainerCoordinates}
					/>
				)}
				{showAlarmQueue && (
					<AlarmQueue
						initialCoordinates={lastAlarmQueueCoordinates}
						parentCoordinates={canvasMainContainerCoordinates}
						parentContainerId={canvasMainContainerId}
						plottedDevices={velocityObjectsReference.current}
					/>
				)}
				<Spin spinning={isLoading} tip={_('Loading')} wrapperClassName='canvasLoading' />
				{currentDeviceForConfiguration}
				{showAlarmDetails && <AlarmDetailsModal onHideModal={() => dispatch(setShowAlarmDetails(undefined))} />}
			</div>
		</>
	);
};

export { MapDesigner };
